/**
 * Soft≠Live Page134 — SA Skills/MCP residual liquid (status dual).
 * Soft≠Live Page166 — SA Skills/MCP residual liquid (page83/134 + status).
 * Mount: /sa/ai-tools
 * Buses:
 *   GET /sa/ai-tools/status (always-on Live posture)
 *   GET/PUT /ai/platform/skills
 *   GET/PUT /ai/platform/mcp-servers
 *   POST …/tools/list · tools/call · PUT allowed-tools · POST skills/{id}/run
 * Soft residual deleted:
 *  - Soft twin AiTools body under sa-models.js
 *  - bare Refresh without Re-read / status SoT
 *  - Soft invent success without Live envelope
 *  - Soft write theatre when seat lacks sa_ai_admin / superadmin
 *  - Soft catalogue invent when AI_PLATFORM unarmed (status honesty first)
 */
import { useMemo, useState } from "react";
import { html, Ico, KV, Card, Field, Input, Alert, Ticker, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { AiStudioDesk } from "./ai-studio-desk.js";

/** Pure: skills rows. */
export function skillRowsOf(pack) {
  const rows = (pack && (pack.items || pack.skills || pack.rows)) || [];
  return Array.isArray(rows) ? rows : [];
}

/** Pure: MCP server rows — prefer Live mcp_id. */
export function mcpRowsOf(pack) {
  const rows = (pack && (pack.items || pack.servers || pack.rows)) || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((s) => {
    const id = String(s.mcp_id || s.id || s.name || "").trim();
    return {
      id,
      name: String(s.name || s.label || id || "—").trim(),
      url: String(s.url || s.endpoint || "").trim(),
      enabled: s.enabled !== false,
      allowed: Array.isArray(s.allowed_tools) ? s.allowed_tools : [],
      scope: String(s.scope || "").trim(),
      raw: s,
    };
  }).filter((s) => s.id);
}

/** Pure: tool names from list-tools Live envelope. */
export function toolNamesOf(pack) {
  if (!pack || typeof pack !== "object") return [];
  const rows = pack.tools || pack.items || pack.listed || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((t) => String((t && (t.name || t.id || t.tool_name)) || "").trim()).filter(Boolean);
}

/** Pure: seat may write/invoke — Soft invent grant deleted. */
export function canWriteAiTools(me) {
  if (!me) return false;
  const role = String(me.role || "").toLowerCase();
  if (role === "superadmin") return true;
  const caps = me.caps || me.sa_capabilities || [];
  const set = new Set((Array.isArray(caps) ? caps : String(caps || "").split(",")).map((c) => String(c || "").toLowerCase().trim()));
  return set.has("sa_ai_admin") || set.has("sa_full_access") || set.has("sa_full");
}

/** Pure: KPI strip from Live envelopes only. */
export function skillsKpisOf(skillItems, servers, listedNames, writeOk) {
  return {
    skills: Array.isArray(skillItems) ? skillItems.length : 0,
    servers: Array.isArray(servers) ? servers.length : 0,
    listed: Array.isArray(listedNames) ? listedNames.length : 0,
    writeOk: !!writeOk,
  };
}

export function AiToolsDesk({ toast }) {
  const posture = useApi(path("get__sa_ai-tools_status"));
  // Fail-closed while loading — product null must not invent open list/write.
  const post = posture.loading || !posture.data ? {} : (posture.data || {});
  const product = post.product_flag === true
    ? true
    : (post.product_flag === false ? false : null);
  const listOk = post.list_allowed === true;
  const writeOk = post.write_allowed === true;
  const detail = post.detail || null;

  const skills = useApi(listOk ? path("get__ai_platform_skills") : "");
  const mcp = useApi(listOk ? path("get__ai_platform_mcp-servers") : "");
  const effectAudit = useApi(writeOk ? (() => {
    try { return path("get__ai_platform_tool-effect-audit"); } catch { return "/ai/platform/tool-effect-audit"; }
  })() : "");
  const { run, busy, error, envelope } = useAction();
  const skillItems = useMemo(() => skillRowsOf(skills.data), [skills.data]);
  const servers = useMemo(() => mcpRowsOf(mcp.data), [mcp.data]);

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [mcpName, setMcpName] = useState("");
  const [mcpUrl, setMcpUrl] = useState("");
  const [pick, setPick] = useState("");
  const [toolName, setToolName] = useState("");
  const [toolArgs, setToolArgs] = useState("{}");
  const [skillRun, setSkillRun] = useState("");
  const [listed, setListed] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const pickId = pick || (servers[0] && servers[0].id) || "";
  const listedNames = toolNamesOf(listed);
  const kpis = useMemo(
    () => skillsKpisOf(skillItems, servers, listedNames, writeOk),
    [skillItems, servers, listedNames, writeOk],
  );
  const tools404 =
    /\b404\b/.test(String(skills.error || "")) ||
    /\b404\b/.test(String(mcp.error || ""));

  const reRead = () => {
    posture.reload && posture.reload();
    skills.reload && skills.reload();
    mcp.reload && mcp.reload();
    toast && toast("Skills posture + buses re-read", "go");
  };

  // Status SoT unarmed (preferred over soft 404 theatre).
  if (!posture.loading && product === false) {
    return html`<div className="sa-skills-desk sa-skills-desk--liquid"
      data-cmc-page="page134-sa-skills" data-cmc-legacy="page83-sa-skills" data-cmc-skills="page48"
      data-cmc-residual="page166-skills">
      <div className="sa-skills-desk__rail">
        <div>
          <p className="eyebrow">Platform · AI team tools</p>
          <h2 className="sa-skills-desk__title">Tools unarmed</h2>
          <p className="note">Skill catalogue stays empty until AI platform is armed.</p>
        </div>
        <div className="btn-row sa-skills-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Re-read posture</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/models")}>Platform models</button>
        </div>
      </div>
      <${Card} title="Skills posture" meta="Live">
        <p className="note">
          product ${detail || "platform tools off"}
          · skills seat follows Live posture
        </p>
      <//>
      <${Alert} tone="warn" title="AI platform tools not armed"
        say="Skill and MCP routes stay empty until the AI platform is armed on this deployment. A brochure skill list is not invented."/>
    </div>`;
  }

  if (listOk && tools404) {
    return html`<div className="sa-skills-desk sa-skills-desk--liquid"
      data-cmc-page="page134-sa-skills" data-cmc-legacy="page83-sa-skills" data-cmc-skills="page48"
      data-cmc-residual="page166-skills">
      <div className="sa-skills-desk__rail">
        <div>
          <p className="eyebrow">Platform · AI team tools</p>
          <h2 className="sa-skills-desk__title">Tools unarmed</h2>
          <p className="note">Skills are not mounted on this deployment yet.</p>
        </div>
        <div className="btn-row sa-skills-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${busy}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Re-read</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/sa/models")}>Platform models</button>
        </div>
      </div>
      <${Alert} tone="warn" title="AI platform tools not armed"
        say="Skill and MCP routes stay empty until the AI platform is armed on this deployment. A brochure skill list is not invented."/>
    </div>`;
  }

  const saveSkill = async () => {
    if (!writeOk) return toast("Skill publish needs an AI admin seat.", "warn");
    if (!name.trim()) return toast("Skill name required", "warn");
    const out = await run(path("put__ai_platform_skills"), {
      body: { scope: "global", name: name.trim(), description: desc, enabled: true },
    });
    if (!out || out.error) {
      toast((out && out.error) || error || "Skill save refused · nothing was changed", "bad");
      return;
    }
    toast("Skill saved · " + name.trim(), "go");
    skills.reload && skills.reload();
    setName("");
    setDesc("");
  };

  const saveMcp = async () => {
    if (!writeOk) return toast("MCP register needs an AI admin seat.", "warn");
    if (!mcpName.trim() || !mcpUrl.trim()) return toast("Name + URL required", "warn");
    const out = await run(path("put__ai_platform_mcp-servers"), {
      body: { name: mcpName.trim(), url: mcpUrl.trim(), enabled: true },
    });
    if (!out || out.error) {
      toast((out && out.error) || error || "MCP save refused · nothing was changed", "bad");
      return;
    }
    toast("Tool server saved · " + mcpName.trim(), "go");
    mcp.reload && mcp.reload();
    setMcpName("");
    setMcpUrl("");
  };

  const listTools = async () => {
    if (!writeOk) return toast("Invoke needs an AI admin seat.", "warn");
    if (!pickId) return toast("Pick a connection first", "warn");
    const out = await run(
      path("post__ai_platform_mcp-servers_p_tools_list", { pathParams: [pickId] }),
      { method: "POST", body: {} },
    );
    if (!out || out.error) {
      toast((out && out.error) || error || "List tools refused · nothing was changed", "bad");
      return;
    }
    setListed(out);
    toast("Tools listed", "go");
  };

  const callTool = async () => {
    if (!writeOk) return toast("Tool call needs an AI admin seat.", "warn");
    if (!pickId || !toolName.trim()) return toast("Pick connection + tool", "warn");
    let argumentsObj = {};
    try {
      argumentsObj = JSON.parse(toolArgs || "{}");
    } catch {
      return toast("Arguments must be JSON", "warn");
    }
    const out = await run(
      path("post__ai_platform_mcp-servers_p_tools_call", { pathParams: [pickId] }),
      { method: "POST", body: { tool_name: toolName.trim(), arguments: argumentsObj } },
    );
    if (!out || out.error) {
      toast((out && out.error) || error || "Tool call refused · nothing was changed", "bad");
      return;
    }
    setReceipt(out);
    toast("Tool ran · " + toolName.trim(), "go");
  };

  const allowListed = async () => {
    if (!writeOk || !pickId || !listed) return;
    const names = toolNamesOf(listed);
    if (!names.length) return toast("No tools in list envelope", "warn");
    const out = await run(
      path("put__ai_platform_mcp-servers_p_allowed-tools", { pathParams: [pickId] }),
      { method: "PUT", body: { allowed_tools: names } },
    );
    if (!out || out.error) {
      toast((out && out.error) || error || "Allow-list refused · nothing was changed", "bad");
      return;
    }
    toast("Allow-list updated", "go");
    mcp.reload && mcp.reload();
  };

  const runSkillNow = async () => {
    if (!writeOk) return toast("Skill run needs an AI admin seat.", "warn");
    const sid = skillRun || (skillItems[0] && (skillItems[0].skill_id || skillItems[0].id || skillItems[0].name));
    if (!sid) return toast("Pick a skill first", "warn");
    const out = await run(path("post__ai_platform_skills_p_run", { pathParams: [sid] }), {
      method: "POST",
      body: {},
    });
    if (!out || out.error) {
      toast((out && out.error) || error || "Skill run refused · nothing was changed", "bad");
      return;
    }
    setReceipt(out);
    toast("Skill ran · " + sid, "go");
  };

  return html`<div className="sa-skills-desk sa-skills-desk--liquid"
    data-cmc-page="page134-sa-skills" data-cmc-legacy="page83-sa-skills" data-cmc-skills="page48"
    data-cmc-residual="page166-skills">
    <${AiStudioDesk} toast=${toast}/>
    <div className="sa-skills-desk__rail">
      <div>
        <p className="eyebrow">Platform · AI team tools</p>
        <h2 className="sa-skills-desk__title">${writeOk ? "Edit for the AI team" : "Read · write gated"}</h2>
        <p className="note">
          Skills + MCP registry are Live buses only. Soft twin under Models stays deleted.
          Posture SoT: Live skills status.
        </p>
      </div>
      <div className="btn-row sa-skills-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || posture.loading}
          onClick=${reRead}><${Ico} d="refresh" w=${14}/> Re-read</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/sa/models")}>Platform models</button>
      </div>
    </div>

    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Card} title="Skills posture" meta="Live">
      ${posture.loading ? html`<p className="note">Reading posture…</p>` : null}
      ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
        say=${String(posture.error)}/>` : null}
      ${!posture.loading && !posture.error ? html`
        <div className="sa-skills-desk__kpis" role="group" aria-label="Skills posture">
          ${[
            ["Product", product ? "flag on" : "—", product ? "1" : "0", "AI_PLATFORM"],
            ["List", listOk ? "open" : "off", listOk ? "1" : "0", "skills"],
            ["Write", writeOk ? "armed" : "gated", writeOk ? "1" : "0", "sa_ai_admin"],
            ["Invoke", writeOk ? "armed" : "gated", writeOk ? "1" : "0", "list/call/run"],
          ].map(([k, v, on, em]) => html`
            <div key=${k} className="sa-skills-desk__kpi" data-on=${on}>
              <span>${k}</span>
              <b>${v}</b>
              <em>${em}</em>
            </div>`)}
        </div>
        ${detail ? html`<p className="note">${detail}</p>` : html`
          <p className="note">MCP and skills only after Live seats open.</p>`}` : null}
    <//>

    <${Alert} tone=${writeOk ? "info" : "warn"}
      title=${writeOk
        ? "Live Skills"
        : "catalogue read · write gated"}
      say=${writeOk
        ? "List/call/allow-list and skill run are Live. Soft twin invent and Soft success without HTTP are deleted."
        : "Session may list when the platform flag is on. PUT / invoke needs superadmin or sa_ai_admin — Soft write theatre is deleted."}/>

    <div className="sa-skills-desk__kpis" role="group" aria-label="Skills KPIs">
      ${[
        ["Skills", kpis.skills, kpis.skills ? "1" : "0", "Live catalogue"],
        ["MCP servers", kpis.servers, kpis.servers ? "1" : "0", "Live registry"],
        ["Write grant", writeOk ? "SA AI" : "read", writeOk ? "1" : "0", writeOk ? "invoke Live" : "gated"],
        ["Listed tools", kpis.listed || "—", kpis.listed ? "1" : "0", listed ? "last list" : "not yet"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="sa-skills-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    <details className="ai-studio__advanced">
      <summary>Advanced · list, allow, run</summary>
    <div className="sa-skills-desk__grid">
      <${Card} title="Skills" meta="Live catalogue"
          sub="Live catalogue. Soft empty invent forbidden — empty stays empty.">
        ${skills.loading
          ? html`<p className="note">Reading skills…</p>`
          : skills.error
            ? html`<${Alert} tone="warn" title="Skills refused" say=${String(skills.error)}/>`
            : !skillItems.length
              ? html`<p className="note">Live empty — no skills published.</p>`
              : html`<div className="rows">
                  ${skillItems.map((s) => {
                    const id = s.skill_id || s.id || s.name;
                    return html`<div key=${id} className="row row--say">
                      <${Ico} d="spark" w=${14}/>
                      <span className="main">
                        <b>${s.name || id}</b>
                        <span>${s.description ? String(s.description).slice(0, 80) : "Markdown playbook"}</span>
                      </span>
                      <span className="chip tiny">${s.enabled === false ? "off" : "on"}</span>
                    </div>`;
                  })}
                </div>`}
        ${writeOk ? html`
        <${Field} label="Name">
          <${Input} icon="brain" value=${name} onInput=${(e) => setName(e.target.value)}
            placeholder="skill.name" disabled=${busy}/>
        <//>
        <${Field} label="Description">
          <${Input} value=${desc} onInput=${(e) => setDesc(e.target.value)}
            placeholder="What it does" disabled=${busy}/>
        <//>
        <button type="button" className="btn btn--go btn--sm" disabled=${busy || !name.trim()}
          onClick=${saveSkill}>Save skill</button>` : null}
      <//>

      <${Card} title="MCP servers" meta="Live registry"
          sub="Ids are Live mcp_id — Soft name-as-id demote deleted.">
        ${mcp.loading
          ? html`<p className="note">Reading MCP…</p>`
          : mcp.error
            ? html`<${Alert} tone="warn" title="MCP refused" say=${String(mcp.error)}/>`
            : !servers.length
              ? html`<p className="note">Live empty — no tool servers.</p>`
              : html`<div className="rows">
                  ${servers.map((s) => html`
                    <button key=${s.id} type="button" className="row" data-on=${pickId === s.id ? "1" : "0"}
                      onClick=${() => setPick(s.id)}>
                      <span className="st"/>
                      <span className="main">
                        <b>${s.name}</b>
                        <span className="m">${s.id}${writeOk && s.url ? (String(s.url).startsWith("vault://") ? " · vault" : " · bound") : ""}</span>
                      </span>
                      <span className="chip tiny" data-tone=${s.enabled ? "ok" : "off"}>
                        ${s.enabled ? "on" : "off"}</span>
                    </button>`)}
                </div>`}
        ${writeOk ? html`
        <${Field} label="Name">
          <${Input} value=${mcpName} onInput=${(e) => setMcpName(e.target.value)}
            placeholder="server-name" disabled=${busy}/>
        <//>
        <${Field} label="Vault">
          <${Input} icon="link" value=${mcpUrl} onInput=${(e) => setMcpUrl(e.target.value)}
            placeholder="vault://ops/egress/…" disabled=${busy}/>
        <//>
        <button type="button" className="btn btn--go btn--sm"
          disabled=${busy || !mcpName.trim() || !mcpUrl.trim()}
          onClick=${saveMcp}>Save connection</button>` : null}
      <//>
    </div>

    ${writeOk ? html`<${Card} title="Invoke gateway" meta="list · call · allow-list · skill run"
        sub="All four verbs are Live when SA AI is armed. Soft toast without response envelope is forbidden.">
      <div className="grid g2">
        <${Field} label="Connection (mcp_id)">
          <select className="inp" value=${pickId} onChange=${(e) => setPick(e.target.value)}
            disabled=${!servers.length}>
            <option value="">Select…</option>
            ${servers.map((s) => html`
              <option key=${s.id} value=${s.id}>${s.name} · ${s.id.slice(0, 12)}</option>`)}
          </select>
        <//>
        <${Field} label="Skill to run">
          <select className="inp" value=${skillRun} onChange=${(e) => setSkillRun(e.target.value)}>
            <option value="">Select…</option>
            ${skillItems.map((s) => {
              const id = s.skill_id || s.id || s.name;
              return html`<option key=${id} value=${id}>${s.name || id}</option>`;
            })}
          </select>
        <//>
      </div>
      <div className="btn-row" style=${{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        <button type="button" className="btn btn--sm" disabled=${busy || !pickId}
          onClick=${listTools}>List tools</button>
        <button type="button" className="btn btn--sm" disabled=${busy || !pickId || !listed}
          onClick=${allowListed}>Allow listed</button>
        <button type="button" className="btn btn--sm" disabled=${busy}
          onClick=${runSkillNow}>Run skill</button>
      </div>
      ${listedNames.length
        ? html`<p className="note" style=${{ marginTop: 8 }}>Listed: ${listedNames.join(", ")}</p>`
        : null}
      <div className="grid g2" style=${{ marginTop: 10 }}>
        <${Field} label="Tool">
          <${Input} value=${toolName} onInput=${(e) => setToolName(e.target.value)}
            placeholder="tool name" disabled=${busy}/>
        <//>
        <${Field} label="Arguments (JSON)">
          <${Input} value=${toolArgs} onInput=${(e) => setToolArgs(e.target.value)}
            placeholder="{}" disabled=${busy}/>
        <//>
      </div>
      <button type="button" className="btn btn--go btn--sm" style=${{ marginTop: 8 }}
        disabled=${busy || !pickId || !toolName.trim()}
        onClick=${callTool}>Call tool</button>
      ${receipt
        ? html`<pre className="note sa-skills-desk__receipt">${JSON.stringify(receipt, null, 2)}</pre>`
        : null}
    <//>` : null}

    ${writeOk ? html`<${Card} title="Effect metadata" meta="SA only · effect_class · hazard · reversibility"
        sub="Fail-closed catalog audit Soft≠ invent. Missing fields block AI eligibility honesty.">
      ${effectAudit.loading
        ? html`<p className="note">Loading effect audit…</p>`
        : effectAudit.error
          ? html`<${Alert} tone="warn" title="Effect audit sealed" say=${String(effectAudit.error)}/>`
          : html`<div>
              <p className="note">
                complete ${String((effectAudit.data && effectAudit.data.complete) || 0)}
                · incomplete ${String((effectAudit.data && effectAudit.data.incomplete) || 0)}
                · soft_invent=${String(!!(effectAudit.data && effectAudit.data.soft_invent))}
              </p>
              ${effectAudit.data && effectAudit.data.sample_mcp_tool
                ? html`<p className="note">
                    sample mcp.tool · effect_class=${effectAudit.data.sample_mcp_tool.effect_class}
                    · hazard=${effectAudit.data.sample_mcp_tool.hazard}
                    · reversibility=${effectAudit.data.sample_mcp_tool.reversibility}
                    · human_only=${String(!!effectAudit.data.sample_mcp_tool.human_only)}
                  </p>`
                : null}
              ${(effectAudit.data && effectAudit.data.gaps || []).slice(0, 8).map((g) => html`
                <p key=${g.capability} className="note">gap · ${g.capability}: ${(g.missing || []).join(", ")}</p>`)}
            </div>`}
    <//>` : null}
    </details>
  </div>`;
}
